/** An offscreen canvas with its 2D context. */
export interface CanvasSurface {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

/**
 * An offscreen canvas a scene paints once and reuses until its `key` changes:
 * a cached backdrop, silhouette or tile.
 */
export interface CanvasLayer extends CanvasSurface {
  /** What the canvas currently holds; empty when stale or released. */
  key: string;
  /** Device pixels per CSS pixel it was painted at; 0 until painted. */
  ratio: number;
}

/** A blank offscreen canvas, or null without a DOM or 2D canvas support. */
export const createSurface = (): CanvasSurface | null => {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  return ctx ? { canvas, ctx } : null;
};

/**
 * A blank, unpainted layer. Scenes that track more about what a layer holds
 * pass their extra fields' starting values (and may override `ratio`).
 */
export function createLayer(): CanvasLayer | null;
export function createLayer<Extra extends object>(extra: Extra): (CanvasLayer & Extra) | null;
export function createLayer(extra?: object): CanvasLayer | null {
  const surface = createSurface();
  return surface ? { ...surface, key: '', ratio: 0, ...extra } : null;
}

/** Frees a layer's pixels now rather than at GC, and marks it unpainted. */
export const releaseLayer = (layer: CanvasLayer | null) => {
  if (!layer) return;
  layer.canvas.width = 0;
  layer.canvas.height = 0;
  layer.key = '';
  layer.ratio = 0;
};

/** Frees an offscreen canvas's pixels now rather than at GC. */
export const releaseCanvas = (canvas: HTMLCanvasElement | null) => {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
};

/** Device pixels per CSS pixel of the context's current transform (1 when unknown). */
export const pixelRatioOf = (ctx: CanvasRenderingContext2D) => {
  const matrix = typeof ctx.getTransform === 'function' ? ctx.getTransform() : null;
  return matrix && Number.isFinite(matrix.a) && matrix.a > 0 ? matrix.a : 1;
};
